/**
 * MinistrareIA - Inbox Page (Simplified)
 * WhatsApp-like interface with SSE real-time updates
 * Business logic moved to backend
 * 
 * UPDATED v3:
 * - Added SyncingModal for first-time connection sync
 * - Improved connection flow: QR Code -> Syncing -> Inbox
 * - NEW: Temporary polling after first connection (15s x 5 times)
 *   to handle Evolution API sync delay
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wifi, WifiOff, Menu, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSE, useSSEEvent, SSE_EVENTS, SSE_STATES } from '../../contexts/SSEContext';

import whatsappService from '../../services/wa-personal/whatsappService';
import chatUtils from '../../utils/wa-personal/chat_utils';
import ConnectionModal from '../../components/wa-personal/ConnectionModal';
import SyncingModal from '../../components/wa-personal/SyncingModal';

import authService from '../../services/global/authService';
import NavSidebar from '../../components/NavSidebar';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';

import LabelsPage from '../global/LabelsPage';
import AgentsPage from '../global/AgentsPage';
import TrainingPage from '../global/TrainingPage';
import FollowUpsPage from '../global/FollowUpsPage';
import LeadsPage from '../global/LeadsPage';
import ProductsPage from '../global/ProductsPage';
import VideoLinksPage from '../global/VideoLinksPage';

// Polling configuration for first connection sync
const SYNC_POLLING_INTERVAL = 15000; // 15 seconds
const SYNC_POLLING_MAX_ATTEMPTS = 5;

const Inbox = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, colors } = useTheme();
  
  // SSE context
  const { isConnected: sseConnected, sseState, reconnectInfo, reconnect: reconnectSSE } = useSSE();

  // Navigation state
  const [currentPage, setCurrentPage] = useState('inbox');

  // Chat state
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ status: 'checking', connected: false });
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [showSyncingModal, setShowSyncingModal] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDisconnectPopup, setShowDisconnectPopup] = useState(true);
  const [isFirstConnection, setIsFirstConnection] = useState(false);

  // Autopilot state
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [blockedContacts, setBlockedContacts] = useState([]);

  // Refs
  const selectedChatRef = useRef(selectedChat);
  const syncPollingRef = useRef(null);
  const syncPollingAttemptsRef = useRef(0);
  const chatsRef = useRef(chats);

  // Keep refs in sync
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopSyncPolling();
    };
  }, []);

  // Load autopilot config on mount
  useEffect(() => {
    const loadAutopilotConfig = async () => {
      try {
        const response = await authService.getAutopilotConfig();
        if (response?.data?.autopilot_contacts) {
          setAutopilotContacts(response.data.autopilot_contacts);
        }
        if (response?.data?.blocked_contacts) {
          setBlockedContacts(response.data.blocked_contacts);
        }
      } catch (err) {
        console.error('[Inbox] Error loading autopilot config:', err);
      }
    };
    loadAutopilotConfig();
  }, []);

  // Handler: toggle autopilot for a specific contact
  const handleToggleAutopilot = useCallback(async (phone, enabled) => {
    try {
      const response = await authService.toggleAutopilotContact(phone, enabled);
      if (response?.data?.autopilot_contacts) {
        setAutopilotContacts(response.data.autopilot_contacts);
      }
      if (response?.data?.blocked_contacts) {
        setBlockedContacts(response.data.blocked_contacts);
      }
    } catch (err) {
      console.error('[Inbox] Error toggling autopilot:', err);
    }
  }, []);

  // Handler: toggle blocked status for a specific contact
  const handleToggleBlockedContact = useCallback(async (phone, blocked) => {
    try {
      const response = await authService.toggleBlockedContact(phone, blocked);
      if (response?.data?.blocked_contacts) {
        setBlockedContacts(response.data.blocked_contacts);
      }
      if (response?.data?.autopilot_contacts) {
        setAutopilotContacts(response.data.autopilot_contacts);
      }
    } catch (err) {
      console.error('[Inbox] Error toggling blocked contact:', err);
    }
  }, []);

  // ============================================
  // SYNC POLLING (for first connection)
  // ============================================

  /**
   * Stop the sync polling
   */
  const stopSyncPolling = useCallback(() => {
    if (syncPollingRef.current) {
      console.log('[Inbox] Stopping sync polling');
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
      syncPollingAttemptsRef.current = 0;
    }
  }, []);

  /**
   * Start temporary polling for first connection
   * Polls every 15s, max 5 times, stops if chats are found
   */
  const startSyncPolling = useCallback(() => {
    // Don't start if already polling
    if (syncPollingRef.current) {
      console.log('[Inbox] Sync polling already active');
      return;
    }

    console.log('[Inbox] Starting sync polling (15s x 5 attempts)');
    syncPollingAttemptsRef.current = 0;

    syncPollingRef.current = setInterval(async () => {
      syncPollingAttemptsRef.current += 1;
      const attempt = syncPollingAttemptsRef.current;

      console.log(`[Inbox] Sync polling attempt ${attempt}/${SYNC_POLLING_MAX_ATTEMPTS}`);

      try {
        const response = await whatsappService.getChats();
        const newChats = response.data || [];

        console.log(`[Inbox] Sync polling got ${newChats.length} chats`);

        if (newChats.length > 0) {
          // Found chats! Update state and stop polling
          console.log('[Inbox] Chats found, stopping sync polling');
          setChats(newChats);
          stopSyncPolling();
          return;
        }
      } catch (err) {
        console.error('[Inbox] Sync polling error:', err);
      }

      // Check if we reached max attempts
      if (attempt >= SYNC_POLLING_MAX_ATTEMPTS) {
        console.log('[Inbox] Max sync polling attempts reached, stopping');
        stopSyncPolling();
      }
    }, SYNC_POLLING_INTERVAL);
  }, [stopSyncPolling]);

  // ============================================
  // SSE EVENT HANDLERS
  // ============================================

  // Handle CONNECTION_UPDATE from SSE
  useSSEEvent(SSE_EVENTS.CONNECTION_UPDATE, (data) => {
    console.log('[Inbox] SSE CONNECTION_UPDATE:', data);
    
    const wasDisconnected = !connectionStatus.connected;
    
    setConnectionStatus({
      status: data.status,
      connected: data.connected,
      phoneNumber: data.phone_number || data.phoneNumber,
    });

    // If just connected and was previously disconnected
    if (data.connected && wasDisconnected) {
      console.log('[Inbox] Connection established, checking if first connection...');
      
      // Check if this is first connection (no chats loaded yet)
      if (chatsRef.current.length === 0 || isFirstConnection) {
        console.log('[Inbox] First connection, showing syncing modal...');
        setShowConnectionModal(false);
        setShowSyncingModal(true);
        loadChatsWithSync();
      } else {
        loadChats(false);
      }
    }
    
    if (!data.connected) {
      setShowDisconnectPopup(true);
      // Stop polling if disconnected
      stopSyncPolling();
    }
  }, [connectionStatus.connected, isFirstConnection, stopSyncPolling]);

  // Handle QRCODE_UPDATED from SSE
  useSSEEvent(SSE_EVENTS.QRCODE_UPDATED, (data) => {
    console.log('[Inbox] SSE QRCODE_UPDATED');
    setConnectionStatus({ status: 'waiting_qr', connected: false });
    if (!showConnectionModal) {
      setShowConnectionModal(true);
    }
  }, [showConnectionModal]);

  // Handle NEW_MESSAGE from SSE
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    console.log('[Inbox] SSE NEW_MESSAGE:', data?.remoteJid);
    
    // If we receive a message, chats are synced - stop polling
    if (syncPollingRef.current) {
      console.log('[Inbox] Received message, stopping sync polling');
      stopSyncPolling();
    }
    
    if (data) {
      updateChatWithNewMessage(data);
    }
  }, [stopSyncPolling]);

  // Handle CHAT_UPDATE from SSE
  useSSEEvent(SSE_EVENTS.CHAT_UPDATE, (data) => {
    console.log('[Inbox] SSE CHAT_UPDATE:', data?.action);
    
    // If we receive chat update, chats are synced - stop polling
    if (syncPollingRef.current) {
      console.log('[Inbox] Received chat update, stopping sync polling');
      stopSyncPolling();
    }
    
    loadChats(true);
  }, [stopSyncPolling]);

  // Handle CHATS_SYNC from SSE (when backend finishes syncing)
  useSSEEvent(SSE_EVENTS.CHATS_SYNC, (data) => {
    console.log('[Inbox] SSE CHATS_SYNC:', data);
    // Reload chats when sync completes
    stopSyncPolling();
    loadChats(true);
    setShowSyncingModal(false);
  }, [stopSyncPolling]);

  // ============================================
  // DATA LOADING
  // ============================================

  const checkConnection = useCallback(async () => {
    try {
      const response = await whatsappService.getConnectionStatus();
      const data = response.data || { status: 'disconnected', connected: false };
      setConnectionStatus(data);
      return data.connected;
    } catch (err) {
      console.error('Error checking connection:', err);
      setConnectionStatus({ status: 'error', connected: false });
      return false;
    }
  }, []);

  const loadChats = useCallback(async (silent = false) => {
    if (!silent) setLoadingChats(true);
    try {
      const response = await whatsappService.getChats();
      const newChats = response.data || [];
      setChats(newChats);
      
      // If we got chats, stop any active polling
      if (newChats.length > 0 && syncPollingRef.current) {
        stopSyncPolling();
      }
    } catch (err) {
      console.error('Error loading chats:', err);
      if (!silent) setChats([]);
    } finally {
      if (!silent) setLoadingChats(false);
    }
  }, [stopSyncPolling]);

  /**
   * Load chats with syncing modal
   * Shows syncing modal while loading, then hides it
   * If chats are empty after loading, starts temporary polling
   */
  const loadChatsWithSync = useCallback(async () => {
    setShowSyncingModal(true);
    setLoadingChats(true);
    
    try {
      // Small delay to show syncing animation
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const response = await whatsappService.getChats();
      const newChats = response.data || [];
      setChats(newChats);
      
      console.log(`[Inbox] loadChatsWithSync got ${newChats.length} chats`);
      
      // Keep syncing modal for a bit longer on first connection
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // If no chats were loaded, start polling
      // Evolution API might still be syncing
      if (newChats.length === 0) {
        console.log('[Inbox] No chats loaded, starting sync polling...');
        startSyncPolling();
      }
      
    } catch (err) {
      console.error('Error loading chats during sync:', err);
      setChats([]);
      // Start polling even on error
      startSyncPolling();
    } finally {
      setLoadingChats(false);
      setShowSyncingModal(false);
      setIsFirstConnection(false);
    }
  }, [startSyncPolling]);

  // ============================================
  // CHAT UPDATE LOGIC (Simplified)
  // ============================================

  const updateChatWithNewMessage = useCallback((messageData) => {
    const { remoteJid, pushName, textContent, timestamp, fromMe, key, messageType, hasMedia } = messageData;
    
    if (!remoteJid) return;
    
    const altJid = key?.remoteJidAlt;
    
    setChats(prevChats => {
      // Find chat using utility
      const existingIndex = chatUtils.findChatIndex(prevChats, remoteJid, altJid);
      
      if (existingIndex >= 0) {
        // Update existing chat
        const newChats = [...prevChats];
        const [existingChat] = newChats.splice(existingIndex, 1);
        
        const updatedChat = {
          ...existingChat,
          // ✅ FIX: Propagate pushName from message to chat (if chat doesn't have one)
          pushName: existingChat.pushName || pushName,
          lastMessage: {
            ...existingChat.lastMessage,
            message: { conversation: textContent },
            key: key,
            messageTimestamp: timestamp,
            // ✅ FIX: Also save pushName in lastMessage for chat_utils fallback
            pushName: pushName,
          },
          updatedAt: timestamp ? new Date(timestamp * 1000).toISOString() : new Date().toISOString(),
          unreadCount: fromMe ? (existingChat.unreadCount || 0) : (existingChat.unreadCount || 0) + 1,
        };
        
        return [updatedChat, ...newChats];
      } else {
        // New chat
        const phoneNumber = remoteJid?.split('@')[0];
        const newChat = {
          id: `new-${Date.now()}`,
          remoteJid,
          pushName: pushName || phoneNumber,
          lastMessage: {
            message: { conversation: textContent },
            key: key,
            messageTimestamp: timestamp,
            pushName: pushName,
          },
          updatedAt: timestamp ? new Date(timestamp * 1000).toISOString() : new Date().toISOString(),
          unreadCount: fromMe ? 0 : 1,
          _phoneNumber: phoneNumber,
          _altJid: altJid,
        };
        
        return [newChat, ...prevChats];
      }
    });
  }, []);

  // ============================================
  // INITIAL LOAD
  // ============================================

  useEffect(() => {
    const init = async () => {
      const isConnected = await checkConnection();
      if (isConnected) {
        await loadChats(false);
      } else {
        // Mark as first connection attempt
        setIsFirstConnection(true);
        setShowConnectionModal(true);
        setLoadingChats(false);
      }
    };
    init();
  }, [checkConnection, loadChats]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Called when ConnectionModal reports successful connection
   * This now triggers the syncing flow
   */
  const handleConnectionSuccess = async () => {
    console.log('[Inbox] handleConnectionSuccess called');
    setShowConnectionModal(false);
    
    // Show syncing modal for first-time connection
    if (isFirstConnection || chatsRef.current.length === 0) {
      await loadChatsWithSync();
    } else {
      await checkConnection();
      await loadChats(false);
    }
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    
    // Clear unread count
    setChats(prevChats => 
      prevChats.map(c => 
        chatUtils.isSameChat(c, chat) ? { ...c, unreadCount: 0 } : c
      )
    );
    
    if (isMobile) setShowSidebar(false);
  };

  const handleBackToChats = () => {
    setSelectedChat(null);
    setShowSidebar(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await checkConnection();
    if (connectionStatus.connected) {
      await loadChats(false);
    }
    setIsRefreshing(false);
  };

  const handleMessageSent = (messageData) => {
    if (selectedChat && messageData) {
      updateChatWithNewMessage({
        remoteJid: selectedChat.remoteJid,
        textContent: messageData.text,
        timestamp: Math.floor(Date.now() / 1000),
        fromMe: true,
        key: { remoteJid: selectedChat.remoteJid, fromMe: true },
      });
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleOpenConnection = () => setShowConnectionModal(true);

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    mainArea: { flex: 1, display: 'flex', overflow: 'hidden' },
    inboxLayout: { flex: 1, display: 'flex', overflow: 'hidden' },
    sidebar: {
      width: isMobile ? '100%' : '395px',
      minWidth: isMobile ? '100%' : '395px',
      maxWidth: isMobile ? '100%' : '395px',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      position: isMobile ? 'fixed' : 'relative',
      left: 0, top: 0, zIndex: 50,
      transform: isMobile && !showSidebar ? 'translateX(-100%)' : 'translateX(0)',
      transition: 'transform 0.3s ease',
    },
    chatArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
      overflow: 'hidden',
    },
    mobileHeader: {
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    mobileMenuBtn: {
      width: 40, height: 40,
      border: 'none', background: 'transparent',
      color: colors.textPrimary, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    mobileTitle: {
      flex: 1, fontWeight: 500, fontSize: 18, textAlign: 'center', color: colors.textPrimary,
    },
    disconnectionPopup: {
      position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)',
      backgroundColor: colors.bgPrimary, color: colors.textPrimary,
      padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12,
      zIndex: 90, borderRadius: 12, border: `1px solid ${colors.danger}`, boxShadow: colors.shadowLg,
    },
    disconnectionBtn: {
      padding: '6px 14px', backgroundColor: colors.danger, color: 'white',
      border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    },
    banner: {
      position: 'fixed', top: 0, left: 0, right: 0,
      color: 'white', padding: '8px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      fontSize: 14, zIndex: 100,
    },
  };

  // ============================================
  // RENDER
  // ============================================

  const renderPageContent = () => {
    switch (currentPage) {
      case 'leads': return <LeadsPage />;
      case 'followups': return <FollowUpsPage />;
      case 'products': return <ProductsPage />;
      case 'video-links': return <VideoLinksPage />;
      case 'labels': return <LabelsPage />;
      case 'agents': return <AgentsPage />;
      case 'training': return <TrainingPage />;
      case 'inbox':
      default:
        return (
          <div style={styles.inboxLayout}>
            <aside style={styles.sidebar}>
              <ChatList
                chats={chats}
                selectedChat={selectedChat}
                onSelectChat={handleSelectChat}
                loading={loadingChats}
              />
            </aside>

            <main style={styles.chatArea}>
              {isMobile && (
                <div style={styles.mobileHeader}>
                  {selectedChat ? (
                    <>
                      <button style={styles.mobileMenuBtn} onClick={handleBackToChats}>
                        <X size={24} />
                      </button>
                      <span style={styles.mobileTitle}>
                        {chatUtils.getChatDisplayName(selectedChat)}
                      </span>
                    </>
                  ) : (
                    <>
                      <button style={styles.mobileMenuBtn} onClick={() => setShowSidebar(true)}>
                        <Menu size={24} />
                      </button>
                      <span style={styles.mobileTitle}>MinistrareIA</span>
                    </>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    {connectionStatus.connected ? (
                      <Wifi size={16} color={colors.accentPrimary} />
                    ) : (
                      <WifiOff size={16} color={colors.danger} />
                    )}
                  </div>
                </div>
              )}

              <ChatWindow
                chat={selectedChat}
                onMessageSent={handleMessageSent}
                autopilotContacts={autopilotContacts}
                onToggleAutopilot={handleToggleAutopilot}
                blockedContacts={blockedContacts}
                onToggleBlockedContact={handleToggleBlockedContact}
              />
            </main>
          </div>
        );
    }
  };

  return (
    <div style={styles.container}>
      {/* SSE Failed Banner */}
      {sseState === SSE_STATES.FAILED && (
        <div style={{ ...styles.banner, backgroundColor: '#ef4444', cursor: 'pointer' }} onClick={reconnectSSE}>
          <WifiOff size={16} />
          <span>Conexão perdida. Clique para reconectar.</span>
        </div>
      )}

      {/* Reconnecting Banner */}
      {reconnectInfo && (
        <div style={{ ...styles.banner, backgroundColor: colors.warning || '#f59e0b' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Reconectando... ({reconnectInfo.attempt}/{reconnectInfo.maxAttempts})</span>
        </div>
      )}

      {/* Navigation Sidebar */}
      <NavSidebar
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onRefresh={handleRefresh}
        onOpenConnection={handleOpenConnection}
        isRefreshing={isRefreshing}
      />

      {/* Main Area */}
      <div style={styles.mainArea}>
        {renderPageContent()}
      </div>

      {/* Connection Modal */}
      <ConnectionModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        onConnected={handleConnectionSuccess}
      />

      {/* Syncing Modal */}
      <SyncingModal
        isOpen={showSyncingModal}
      />

      {/* Disconnection Popup */}
      {currentPage === 'inbox' && 
       !connectionStatus.connected && 
       connectionStatus.status !== 'checking' && 
       !showConnectionModal &&
       !showSyncingModal &&
       showDisconnectPopup && (
        <div style={styles.disconnectionPopup}>
          <WifiOff size={18} style={{ color: colors.danger }} />
          <span style={{ fontSize: 14, color: colors.textSecondary }}>WhatsApp desconectado</span>
          <button style={styles.disconnectionBtn} onClick={() => setShowConnectionModal(true)}>
            Reconectar
          </button>
          <button 
            style={{ padding: 4, background: 'transparent', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
            onClick={() => setShowDisconnectPopup(false)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { overflow: hidden; }
      `}</style>
    </div>
  );
};

export default Inbox;